import {Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {CurrencyService} from 'src/app/services/currency/currency.service';
import {StaticValuesUtils} from 'src/app/utils/static-values/static-values.utils';
import {Item} from '../models/domain/item';
import {Location} from '@angular/common';
import {Currency, Data, Discount, Favorite, Price,} from '../models/favorites/favorite';
import {PriceFormatterPipe} from 'src/app/utils/priceFormatter.pipe';
import {URLParamsConstants} from 'src/app/utils/constants/UrlConstants';
import {CalculationTextPipe} from 'src/app/pipes/calculations-text.pipe';
import {AppSettingsService} from 'src/app/services/app-settings/app-settings.service';
import '@trip-planner/components';
import {CookieService} from '@shopping-angular-components/common-library';
import {CROSS_SELLING_COUPON_COOKIE_NAME} from 'src/app/utils/constants/constants';
import {ProductUtilsService} from '../../services/product-utils.service';

declare var window;

@Component({
    selector: 'favorite-manager',
    templateUrl: './favorite-manager.component.html',
    styleUrls: ['./favorite-manager.component.scss'],
    providers: [PriceFormatterPipe, CalculationTextPipe]
})
export class FavoriteManagerComponent implements OnInit {
    @Input() cityGid: string;
    @Input() item: Partial<Item>;

    product: string;
    xClient: string;

    discountTypeCoupon = 'COUPON';
    discountTypeCommon = 'COMMON';

    favData: Favorite;
    forceSaveFavorite: boolean;
    isMobile: boolean = StaticValuesUtils.isMobile();
    isLoggedInUser: boolean;
    locale: string;
    selectedFavId?: string;
    pageviewId: string = StaticValuesUtils.getPageViewId();
    flow: string;
    tripPlannerEnabled: boolean = StaticValuesUtils.getTripPlannerEnabled();
    
    constructor(
        private activatedRoute: ActivatedRoute,
        private currencyService: CurrencyService,
        private location: Location,
        private translateService: TranslateService,
        private priceFormatterPipe: PriceFormatterPipe,
        private calculationTextPipe: CalculationTextPipe,
        private appSettingsService: AppSettingsService,
        private cookieService: CookieService,
        private productUtilsService: ProductUtilsService,
    ) {
        this.isLoggedInUser = false;
        this.flow = this.appSettingsService.getFlow();
    }

    ngOnInit(): void {
        this.product = 'transfer';
        this.xClient = this.appSettingsService.getAppName();
        this.locale = this.appSettingsService.getLocale();
        this.favData = this.getFavData()
        const isFavs =
            this.activatedRoute.snapshot.queryParams[URLParamsConstants.REFERER_EVENT] === URLParamsConstants.FAV;

        if (isFavs) this.removeFavoriteParameters();
    }

    getViewType(): string {
        return StaticValuesUtils.isWebview ? 'WEB_VIEW' : 'WEB';
    }

    private removeFavoriteParameters() {
        const currentUrl = this.location.path();
        const urlObject = new URL(currentUrl, window.location.origin);
        const newUrl = urlObject.pathname;
        this.location.replaceState(newUrl);
    }

    getFavData(): Favorite {
        const item = this.item;
        let distribution = null;
        let favorite: Favorite = {
            url: '',
            external_id: '',
            distribution: '',
            destination_gid: '',
            data: undefined,
        };
        let data: Data = {
            title: '',
            subtitle: '',
            price: undefined,
            from_airport: undefined,
        };
        let price: Price = {
            currency: undefined,
            total: '',
        };
        let currency: Currency = {
            code: '',
            mask: '',
        };
        let discount: Discount = {
            total: '',
            amount: '',
            type: '',
        };

        this.activatedRoute.paramMap.subscribe(params => {
            distribution = params.get('distribution');
        });

        data.characteristics = this.productUtilsService.getCharacteristics(item);
        data.title = this.translateService.instant('transfer-cluster.service.type.' + item.transferServiceType);
        data.subtitle = item.transferDescription;
        data.images = [item.image.key];

        currency.code = this.currencyService.selectedCurrency.value;
        currency.mask = StaticValuesUtils.getCurrencySymbol(currency.code);
        price.currency = currency;
        price.total = this.priceFormatterPipe.transform(item.prices[currency.code].totalPrice);

        if (item.promotion) {
            if (
                StaticValuesUtils.getIsPigDeal() &&
                this.cookieService.get(CROSS_SELLING_COUPON_COOKIE_NAME) === 'true'
            ) {
                discount.type = this.discountTypeCoupon;
            } else {
                discount.type = this.discountTypeCommon;
            }
            discount.amount = `- ${item.promotion.percentage}%`;
            discount.total = this.priceFormatterPipe.transform(item.prices[currency.code].originalPrice);
            price.discount = discount;
        }

        data.price = price;

        data.details = this.productUtilsService.getDetails(item);
        data.from_airport = this.productUtilsService.getFromAirport(item);

        if (item.returnSegment) data.to_airport = this.productUtilsService.getToAirport(item);

        if (item.serviceType) data.sale_drivers = this.productUtilsService.getSaleDriver(item);

        favorite.data = data;
        favorite.distribution = distribution;
        favorite.external_id = this.getExternalId(distribution);
        favorite.url = this.productUtilsService.getUrl(item);
        favorite.destination_gid = this.cityGid;
        favorite.origin_gid = this.cityGid;
        
        return favorite;
    }

    getExternalId(distribution: string): string {
        const parts: (string | undefined)[] = [
            this.item.serviceId,
            this.item.outwardSegment?.pickupDatetime?.toString(),
            this.item.returnSegment?.pickupDatetime?.toString(),
            distribution
        ];

        return parts.filter(part => part != null).join("_");
    }
}
